/*
 * ClusterAutoscalerPanel — symmetric to KarpenterPanel for clusters where
 * autoscaler_kind is "ca" (self-deployed Cluster Autoscaler) or
 * "gke-native" (GKE-managed control-plane autoscaler).
 *
 * Read surface: list of cloud-managed node groups (name, machine type,
 * min/max/desired, spot vs on-demand, status). Backed by
 * GET /v1/clusters/{cid}/ca/nodegroups, populated every 60s by the agent.
 *
 * Write surfaces (all gated by the operator-approval rail with
 * approved_by="manual" so the agent picks them up without separate
 * approval, mirroring the Karpenter panel):
 *
 *   Per-nodegroup
 *     - Resize           → POST .../ca/nodegroups/{name}/resize
 *     - Edit autoscaling → POST .../ca/nodegroups/{name}/autoscaling
 *
 *   Cluster-wide
 *     - Optimize CA args         → POST .../cluster-autoscaler/optimize-args
 *     - Optimize priority expander → POST .../cluster-autoscaler/priority-expander/optimize
 *     - Set GKE autoscaling profile → POST .../cluster-autoscaler/autoscaling-profile  (GCP only)
 *
 * Visibility is handled at the page level (NodeOptimization.js gates by
 * `isKarpenter`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

const REFRESH_MS = 60_000;

const formatStaleness = (s) => {
  if (s === null || s === undefined) return '—';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

// ─── Per-nodegroup: Resize ────────────────────────────────────────────
const ResizeDialog = ({ open, onOpenChange, group, onSubmit, submitting }) => {
  const [desired, setDesired] = useState(group?.live_node_count ?? group?.initial_node_count ?? 0);
  useEffect(() => {
    setDesired(group?.live_node_count ?? group?.initial_node_count ?? 0);
  }, [group]);
  if (!group) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resize node group: {group.name}</DialogTitle>
          <DialogDescription>
            Current size {group.live_node_count ?? group.initial_node_count ?? '?'}, autoscaling range
            [{group.min_nodes ?? '?'}, {group.max_nodes ?? '?'}]. The agent
            calls the cloud SDK's setSize on the next 30s tick. Identical
            value is a no-op.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="desired_size">Desired size</Label>
          <Input
            id="desired_size"
            type="number"
            min={0}
            value={desired}
            onChange={(e) => setDesired(Math.max(0, Number(e.target.value)))}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit(desired)} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit resize'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Per-nodegroup: Edit autoscaling bounds ───────────────────────────
const AutoscalingDialog = ({ open, onOpenChange, group, onSubmit, submitting }) => {
  const [enabled, setEnabled] = useState(group?.autoscaling_enabled ?? false);
  const [minNodes, setMinNodes] = useState(group?.min_nodes ?? 0);
  const [maxNodes, setMaxNodes] = useState(group?.max_nodes ?? 0);
  useEffect(() => {
    setEnabled(group?.autoscaling_enabled ?? false);
    setMinNodes(group?.min_nodes ?? 0);
    setMaxNodes(group?.max_nodes ?? 0);
  }, [group]);
  if (!group) return null;
  const invalid = enabled && (maxNodes < minNodes || minNodes < 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit autoscaling: {group.name}</DialogTitle>
          <DialogDescription>
            Updates the cloud-managed autoscaling bounds. AWS routes to
            ASG UpdateAutoScalingGroup; GCP routes to
            container.SetNodePoolAutoscaling.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
            <div>
              <Label htmlFor="autoscaling-enabled" className="text-sm font-medium">Autoscaling enabled</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When off, min/max are ignored and the group runs at a fixed size.
              </p>
            </div>
            <Switch
              id="autoscaling-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>
          {enabled && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="min_nodes">Min nodes</Label>
                <Input
                  id="min_nodes"
                  type="number"
                  min={0}
                  value={minNodes}
                  onChange={(e) => setMinNodes(Math.max(0, Number(e.target.value)))}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="max_nodes">Max nodes</Label>
                <Input
                  id="max_nodes"
                  type="number"
                  min={0}
                  value={maxNodes}
                  onChange={(e) => setMaxNodes(Math.max(0, Number(e.target.value)))}
                />
              </div>
            </div>
          )}
          {invalid && (
            <p className="text-xs text-red-600">max_nodes must be ≥ min_nodes ≥ 0</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onSubmit({ enabled, min_nodes: minNodes, max_nodes: maxNodes })}
            disabled={submitting || invalid}
          >
            {submitting ? 'Submitting…' : 'Submit autoscaling update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Cluster-wide: Consolidation (focused control on scale-down timing) ──
// Symmetric to Karpenter's "Optimize Disruption" button. Operates on three
// CA args that together define consolidation behavior:
//   --scale-down-unneeded-time       how long a node must be idle before removal
//   --scale-down-delay-after-add     cool-off after scale-up before any removal
//   --scale-down-utilization-threshold  per-node usage % below which CA
//                                       considers the node a removal candidate
//
// Presets translate operator intent (cost / balanced / latency) into the
// underlying values; the "Custom" tab keeps the raw inputs for power users.
const CONSOLIDATION_PRESETS = {
  aggressive: {
    label: 'Aggressive (cost first)',
    description: 'Consolidate fast — drop idle nodes within ~5 min. Best for batch / dev clusters.',
    args: {
      'scale-down-unneeded-time': '5m',
      'scale-down-delay-after-add': '5m',
      'scale-down-utilization-threshold': '0.6',
    },
  },
  balanced: {
    label: 'Balanced',
    description: 'CA defaults. ~10 min idle window, conservative threshold.',
    args: {
      'scale-down-unneeded-time': '10m',
      'scale-down-delay-after-add': '10m',
      'scale-down-utilization-threshold': '0.5',
    },
  },
  conservative: {
    label: 'Conservative (latency first)',
    description: 'Hold nodes longer to absorb traffic spikes — ~20 min idle window. Best for prod / latency-sensitive.',
    args: {
      'scale-down-unneeded-time': '20m',
      'scale-down-delay-after-add': '15m',
      'scale-down-utilization-threshold': '0.4',
    },
  },
};

// Maps our preset → GKE's Cluster.autoscaling.autoscalingProfile.
// GKE only exposes two values; "conservative" maps to BALANCED with a
// note in the UI explaining the limitation.
const PRESET_TO_GKE_PROFILE = {
  aggressive: 'OPTIMIZE_UTILIZATION',
  balanced: 'BALANCED',
  conservative: 'BALANCED',
};

const ConsolidationDialog = ({ open, onOpenChange, onSubmit, submitting, clusterId, cloudProvider }) => {
  const isGCP = cloudProvider === 'gcp';
  const [preset, setPreset] = useState('balanced');
  const [customMode, setCustomMode] = useState(false);
  const [unneededTime, setUnneededTime] = useState('10m');
  const [delayAfterAdd, setDelayAfterAdd] = useState('10m');
  const [utilThreshold, setUtilThreshold] = useState(0.5);
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);

  // Fetch the data-driven recommendation each time the dialog opens.
  // Pre-selects the recommended preset so the operator sees the
  // utilization-based suggestion before they pick. Cached only for the
  // current open; fresh fetch on next open keeps the signals current.
  useEffect(() => {
    if (!open || !clusterId) return;
    let cancelled = false;
    setRecLoading(true);
    axios.get(`${API}/v1/clusters/${clusterId}/cluster-autoscaler/consolidation-recommendation`)
      .then((resp) => {
        if (cancelled) return;
        const r = resp.data;
        setRec(r);
        if (r?.preset && CONSOLIDATION_PRESETS[r.preset]) {
          setPreset(r.preset);
          setCustomMode(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setRec(null);
      })
      .finally(() => { if (!cancelled) setRecLoading(false); });
    return () => { cancelled = true; };
  }, [open, clusterId]);

  // Sync custom inputs when preset changes (so flipping back-and-forth feels right)
  useEffect(() => {
    if (!customMode) {
      const args = CONSOLIDATION_PRESETS[preset].args;
      setUnneededTime(args['scale-down-unneeded-time']);
      setDelayAfterAdd(args['scale-down-delay-after-add']);
      setUtilThreshold(Number(args['scale-down-utilization-threshold']));
    }
  }, [preset, customMode]);

  const handleApply = () => {
    if (isGCP) {
      // GKE-managed CA: there's no Deployment to patch. Route to the
      // cluster-wide autoscaling-profile API instead. Custom-mode
      // arg edits are ignored here — GKE doesn't expose those knobs.
      onSubmit({
        gke_profile: PRESET_TO_GKE_PROFILE[preset] || 'BALANCED',
        preset,
      });
      return;
    }
    const args = customMode
      ? {
          'scale-down-unneeded-time': unneededTime,
          'scale-down-delay-after-add': delayAfterAdd,
          'scale-down-utilization-threshold': String(utilThreshold),
        }
      : CONSOLIDATION_PRESETS[preset].args;
    onSubmit({ args });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Optimize consolidation</DialogTitle>
          <DialogDescription>
            Controls how aggressively the Cluster Autoscaler removes idle
            nodes. Patches three CA Deployment args on the next agent tick.
            On GKE, you can also set a cluster-wide profile (Set GKE
            profile) for similar effect.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Data-driven recommendation banner. Fetched on dialog open
              from /cluster-autoscaler/consolidation-recommendation. The
              recommended preset is pre-selected below — operator can
              accept (just click Apply) or override by clicking a
              different chip. */}
          {recLoading && (
            <div className="rounded-md border border-slate-200 bg-slate-50 dark:bg-surface-dark p-3 text-xs text-slate-500">
              Analyzing cluster utilization…
            </div>
          )}
          {!recLoading && rec && (
            <div className="rounded-md border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                  Recommended: {rec.preset_label || rec.preset}
                </div>
                <Badge variant="outline" className="text-[10px]">
                  confidence: {rec.confidence}
                </Badge>
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-200">{rec.reasoning}</p>
              {rec.signals && rec.signals.sample_count > 0 && (
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 flex flex-wrap gap-x-3">
                  <span>CPU 24h avg: {rec.signals.cpu_pct_avg_24h?.toFixed(1) ?? '—'}%</span>
                  <span>MEM 24h avg: {rec.signals.mem_pct_avg_24h?.toFixed(1) ?? '—'}%</span>
                  <span>Churn 14d: {rec.signals.churn_actions_14d ?? 0}</span>
                  <span>Samples: {rec.signals.sample_count}</span>
                </div>
              )}
            </div>
          )}

          {/* Preset chips */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Preset</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(CONSOLIDATION_PRESETS).map(([key, p]) => {
                const isRecommended = rec?.preset === key;
                const isSelected = !customMode && preset === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setPreset(key); setCustomMode(false); }}
                    className={`text-left rounded-md border p-3 text-sm transition-colors relative ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                        : 'border-slate-200 dark:border-surface-border hover:bg-slate-50 dark:hover:bg-surface-dark'
                    }`}
                  >
                    <div className="font-medium flex items-center gap-1.5">
                      {p.label.split(' (')[0]}
                      {isRecommended && (
                        <Badge variant="secondary" className="text-[9px] px-1 py-0">★</Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">{p.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* GKE-managed clusters: explain the routing change. The CA
              Deployment doesn't exist on GKE — these args are advisory;
              we actually submit the equivalent GKE cluster profile via
              container.UpdateCluster. */}
          {isGCP && (
            <div className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs">
              <div className="font-medium text-amber-900 dark:text-amber-200">
                GKE-managed cluster — routing to autoscaling-profile API
              </div>
              <p className="text-amber-800/80 dark:text-amber-200/80 mt-1 leading-relaxed">
                GKE runs cluster-autoscaler on the managed control plane, so the raw
                CA args below can't be patched directly. The preset translates to a
                cluster-wide profile via{' '}
                <code>container.UpdateCluster</code>:{' '}
                <strong>aggressive → OPTIMIZE_UTILIZATION</strong>,{' '}
                <strong>balanced / conservative → BALANCED</strong>.
                GKE doesn't expose a slower-than-default profile, so conservative
                maps to BALANCED with a tighter NAP limit recommendation only.
              </p>
            </div>
          )}

          {/* Resolved values (read-only when preset, editable in custom mode) */}
          <div className="rounded-md border border-slate-200 dark:border-surface-border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                {isGCP ? 'Equivalent args (advisory only on GKE)' : 'Args'}
              </Label>
              {!isGCP && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs"
                  onClick={() => setCustomMode((v) => !v)}
                >
                  {customMode ? '← use a preset' : 'customize ▾'}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Unneeded time</Label>
                <Input
                  value={unneededTime}
                  onChange={(e) => setUnneededTime(e.target.value)}
                  disabled={!customMode}
                />
                <p className="text-[11px] text-muted-foreground">--scale-down-unneeded-time</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Delay after add</Label>
                <Input
                  value={delayAfterAdd}
                  onChange={(e) => setDelayAfterAdd(e.target.value)}
                  disabled={!customMode}
                />
                <p className="text-[11px] text-muted-foreground">--scale-down-delay-after-add</p>
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Utilization threshold</Label>
                <Input
                  type="number" step="0.05" min={0} max={1}
                  value={utilThreshold}
                  onChange={(e) => setUtilThreshold(Number(e.target.value))}
                  disabled={!customMode}
                />
                <p className="text-[11px] text-muted-foreground">
                  --scale-down-utilization-threshold. Nodes below this % are removal candidates.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleApply} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Cluster-wide: Advanced CA args (catch-all, includes bulk-delete) ────
const CAArgsDialog = ({ open, onOpenChange, onSubmit, submitting }) => {
  const [scaleDownUtil, setScaleDownUtil] = useState(0.5);
  const [scaleDownUnneeded, setScaleDownUnneeded] = useState('10m');
  const [scaleDownDelayAfterAdd, setScaleDownDelayAfterAdd] = useState('10m');
  const [maxEmptyBulkDelete, setMaxEmptyBulkDelete] = useState(10);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Optimize Cluster Autoscaler args</DialogTitle>
          <DialogDescription>
            Patches the <code>kube-system/cluster-autoscaler</code> Deployment
            args. Lowering the scale-down utilization threshold consolidates
            more aggressively. Bulk-delete bumps speed up large scale-downs.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1">
            <Label>Scale-down utilization threshold</Label>
            <Input
              type="number" step="0.05" min={0} max={1}
              value={scaleDownUtil}
              onChange={(e) => setScaleDownUtil(Number(e.target.value))}
            />
            <p className="text-[11px] text-muted-foreground">CA default 0.5; lower = more consolidation</p>
          </div>
          <div className="space-y-1">
            <Label>Scale-down unneeded time</Label>
            <Input
              value={scaleDownUnneeded}
              onChange={(e) => setScaleDownUnneeded(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">e.g. 10m, 5m</p>
          </div>
          <div className="space-y-1">
            <Label>Scale-down delay after add</Label>
            <Input
              value={scaleDownDelayAfterAdd}
              onChange={(e) => setScaleDownDelayAfterAdd(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Max empty bulk delete</Label>
            <Input
              type="number" min={1}
              value={maxEmptyBulkDelete}
              onChange={(e) => setMaxEmptyBulkDelete(Math.max(1, Number(e.target.value)))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onSubmit({
              args: {
                'scale-down-utilization-threshold': String(scaleDownUtil),
                'scale-down-unneeded-time': scaleDownUnneeded,
                'scale-down-delay-after-add': scaleDownDelayAfterAdd,
                'max-empty-bulk-delete': String(maxEmptyBulkDelete),
              },
            })}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Cluster-wide: Optimize priority expander ─────────────────────────
const PriorityExpanderDialog = ({ open, onOpenChange, onSubmit, submitting }) => {
  // Default: prefer spot (priority 50) over on-demand (10). Patterns are
  // regex matched against node-group names.
  const [rows, setRows] = useState([
    { priority: '50', pattern: '.*spot.*' },
    { priority: '10', pattern: '.*on-demand.*|.*standard.*' },
  ]);
  const updateRow = (idx, field, value) => {
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };
  const addRow = () => setRows((r) => [...r, { priority: '20', pattern: '' }]);
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Optimize priority-expander ConfigMap</DialogTitle>
          <DialogDescription>
            Patches <code>kube-system/cluster-autoscaler-priority-expander</code>.
            CA must be configured with <code>--expander=priority</code> for
            this to take effect. Higher priority wins.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-3">
                <Input
                  type="number" min={0}
                  value={row.priority}
                  onChange={(e) => updateRow(i, 'priority', e.target.value)}
                  placeholder="priority"
                />
              </div>
              <div className="col-span-8">
                <Input
                  value={row.pattern}
                  onChange={(e) => updateRow(i, 'pattern', e.target.value)}
                  placeholder="regex pattern e.g. .*spot.*"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="col-span-1 text-red-500"
                onClick={() => removeRow(i)}
                disabled={rows.length === 1}
              >×</Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow}>+ add row</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const priorities = {};
              for (const r of rows) {
                if (!r.priority || !r.pattern) continue;
                if (!priorities[r.priority]) priorities[r.priority] = [];
                priorities[r.priority].push(r.pattern);
              }
              onSubmit({ priorities });
            }}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Cluster-wide (GCP only): Set GKE autoscaling profile ─────────────
const GKEProfileDialog = ({ open, onOpenChange, onSubmit, submitting }) => {
  const [profile, setProfile] = useState('OPTIMIZE_UTILIZATION');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set GKE autoscaling profile</DialogTitle>
          <DialogDescription>
            Cluster-wide setting on GKE clusters only. OPTIMIZE_UTILIZATION
            consolidates aggressively (preferred for cost). BALANCED keeps
            more headroom (preferred for latency-sensitive workloads).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Profile</Label>
          <Select value={profile} onValueChange={setProfile}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="OPTIMIZE_UTILIZATION">OPTIMIZE_UTILIZATION (cost)</SelectItem>
              <SelectItem value="BALANCED">BALANCED (latency)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ profile })} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PendingBadge = ({ pending, label }) => {
  if (!pending) return null;
  return (
    <Badge variant="secondary" className="ml-2">
      {label} in-flight ({pending.status})
    </Badge>
  );
};

const ClusterAutoscalerPanel = ({ clusterId }) => {
  const [data, setData] = useState({ node_groups: [], staleness_seconds: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [resizeGroup, setResizeGroup] = useState(null);
  const [autoscalingGroup, setAutoscalingGroup] = useState(null);
  const [showConsolidation, setShowConsolidation] = useState(false);
  const [showCAArgs, setShowCAArgs] = useState(false);
  const [showPriorityExpander, setShowPriorityExpander] = useState(false);
  const [showGKEProfile, setShowGKEProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchInventory = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/v1/clusters/${clusterId}/ca/nodegroups`);
      setData(resp.data || { node_groups: [] });
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'failed to fetch');
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    fetchInventory();
    const t = setInterval(fetchInventory, REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchInventory]);

  const submitResize = async (desired) => {
    if (!resizeGroup) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/v1/clusters/${clusterId}/ca/nodegroups/${resizeGroup.name}/resize`,
        { desired_size: desired },
      );
      toast.success(`Resize submitted for ${resizeGroup.name}`);
      setResizeGroup(null);
      fetchInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'resize failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitAutoscaling = async (payload) => {
    if (!autoscalingGroup) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/v1/clusters/${clusterId}/ca/nodegroups/${autoscalingGroup.name}/autoscaling`,
        payload,
      );
      toast.success(`Autoscaling update submitted for ${autoscalingGroup.name}`);
      setAutoscalingGroup(null);
      fetchInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'autoscaling update failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Both consolidation + advanced-args submit to the same backend endpoint.
  // The distinction is purely UI: presets + 3 inputs vs full 4-input form.
  const submitArgs = async (payload, source) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/v1/clusters/${clusterId}/cluster-autoscaler/optimize-args`,
        payload,
      );
      toast.success(`${source} submitted`);
      setShowConsolidation(false);
      setShowCAArgs(false);
      fetchInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${source} submit failed`);
    } finally {
      setSubmitting(false);
    }
  };

  // Consolidation submit branches on payload shape. The ConsolidationDialog
  // sends `{gke_profile, preset}` for GKE clusters (CA Deployment doesn't
  // exist there) and `{args}` for self-deployed CA. This lets one button
  // do the right thing on both cluster types without operator awareness.
  const submitConsolidation = async (payload) => {
    if (payload?.gke_profile) {
      setSubmitting(true);
      try {
        await axios.post(
          `${API}/v1/clusters/${clusterId}/cluster-autoscaler/autoscaling-profile`,
          { profile: payload.gke_profile },
        );
        toast.success(
          `GKE profile submitted: ${payload.gke_profile} (preset=${payload.preset})`
        );
        setShowConsolidation(false);
        fetchInventory();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'GKE profile submit failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    return submitArgs(payload, 'Consolidation update');
  };
  const submitCAArgs = (payload) => submitArgs(payload, 'CA args update');

  const submitPriorityExpander = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/v1/clusters/${clusterId}/cluster-autoscaler/priority-expander/optimize`,
        payload,
      );
      toast.success('Priority expander update submitted');
      setShowPriorityExpander(false);
      fetchInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'priority expander submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitGKEProfile = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/v1/clusters/${clusterId}/cluster-autoscaler/autoscaling-profile`,
        payload,
      );
      toast.success(`GKE profile set: ${payload.profile}`);
      setShowGKEProfile(false);
      fetchInventory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'GKE profile set failed');
    } finally {
      setSubmitting(false);
    }
  };

  const empty = !loading && (data.node_groups || []).length === 0;
  const kind = data.autoscaler_kind && data.autoscaler_kind !== '?' ? data.autoscaler_kind : null;
  const cloudProvider = data.cloud_provider || null;
  const isGCP = cloudProvider === 'gcp';
  const lastSync = formatStaleness(data.staleness_seconds);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-base">
          <span>Cluster Autoscaler</span>
          {kind && <Badge variant="outline" className="font-mono text-[10px]">{kind}</Badge>}
          <span className="text-xs font-normal text-muted-foreground ml-auto">
            last sync: {lastSync}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Cluster-wide action bar.
            Consolidation is the primary cost knob (most operators only
            ever touch this); GKE profile is the GCP-managed equivalent
            and complements consolidation rather than replacing it; the
            rest are power-user / cluster-shape level controls. */}
        {!loading && (
          <div className="flex flex-wrap gap-2 mb-4">
            <Button size="sm" onClick={() => setShowConsolidation(true)}>
              ★ Auto-optimize consolidation
            </Button>
            {isGCP && (
              <Button size="sm" variant="outline" onClick={() => setShowGKEProfile(true)}>
                Set GKE profile
              </Button>
            )}
            {/* Advanced CA args + Priority expander patch the
                in-cluster cluster-autoscaler Deployment / ConfigMap.
                On GKE-managed CA those resources live on the control
                plane and are not user-patchable — the backend 400s on
                emit and the action would always fail. Hide here so the
                operator doesn't have to discover that by clicking. */}
            {kind !== "gke-native" && (
              <>
                <Button size="sm" variant="outline" onClick={() => setShowCAArgs(true)}>
                  Advanced CA args
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowPriorityExpander(true)}>
                  Priority expander
                </Button>
              </>
            )}
          </div>
        )}

        {error && (
          <div className="mb-3 text-sm text-red-600">Error: {error}</div>
        )}
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && empty && (
          <div className="text-sm text-muted-foreground">
            Waiting for the agent to report node groups. The CA inventory
            ticker posts every 60s. If this stays empty, verify the agent
            has cloud credentials wired — either a service-account secret
            (the <code className="mx-1">cloudAuth.gcp.credentialsSecret</code> /
            <code className="mx-1">cloudAuth.aws.credentialsSecret</code> helm
            values) or Workload Identity (GCP) / IRSA (AWS) bound to the
            agent's ServiceAccount, and that the agent runs with
            <code className="mx-1">mode=optimize</code>.
          </div>
        )}
        {!empty && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Machine type</TableHead>
                <TableHead>Spot</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Autoscaling</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.node_groups || []).map((g) => (
                <TableRow key={g.name}>
                  <TableCell className="font-mono text-xs">{g.name}</TableCell>
                  <TableCell>{g.machine_type || '—'}</TableCell>
                  <TableCell>
                    {g.is_spot ? (
                      <Badge variant="secondary">spot</Badge>
                    ) : (
                      <Badge variant="outline">on-demand</Badge>
                    )}
                  </TableCell>
                  <TableCell>{g.live_node_count ?? g.initial_node_count ?? '—'}</TableCell>
                  <TableCell>
                    {g.autoscaling_enabled
                      ? `${g.min_nodes ?? 0} – ${g.max_nodes ?? '?'}`
                      : 'disabled'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={g.status === 'RUNNING' ? 'default' : 'secondary'}>
                      {g.status || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setResizeGroup(g)}
                        disabled={Boolean(g.pending_resize)}
                      >
                        Resize
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAutoscalingGroup(g)}
                        disabled={Boolean(g.pending_autoscaling)}
                      >
                        Autoscaling
                      </Button>
                    </div>
                    <div className="mt-1">
                      <PendingBadge pending={g.pending_resize} label="resize" />
                      <PendingBadge pending={g.pending_autoscaling} label="autoscaling" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <ResizeDialog
        open={Boolean(resizeGroup)}
        onOpenChange={(v) => { if (!v) setResizeGroup(null); }}
        group={resizeGroup}
        onSubmit={submitResize}
        submitting={submitting}
      />
      <AutoscalingDialog
        open={Boolean(autoscalingGroup)}
        onOpenChange={(v) => { if (!v) setAutoscalingGroup(null); }}
        group={autoscalingGroup}
        onSubmit={submitAutoscaling}
        submitting={submitting}
      />
      <ConsolidationDialog
        open={showConsolidation}
        onOpenChange={setShowConsolidation}
        onSubmit={submitConsolidation}
        submitting={submitting}
        clusterId={clusterId}
        cloudProvider={cloudProvider}
      />
      <CAArgsDialog
        open={showCAArgs}
        onOpenChange={setShowCAArgs}
        onSubmit={submitCAArgs}
        submitting={submitting}
      />
      <PriorityExpanderDialog
        open={showPriorityExpander}
        onOpenChange={setShowPriorityExpander}
        onSubmit={submitPriorityExpander}
        submitting={submitting}
      />
      <GKEProfileDialog
        open={showGKEProfile}
        onOpenChange={setShowGKEProfile}
        onSubmit={submitGKEProfile}
        submitting={submitting}
      />
    </Card>
  );
};

export default ClusterAutoscalerPanel;
